import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { useTotp } from '../api/hooks';
import { Button } from '../components/ui';
import { getTg } from '../lib/tg';
import { useT } from '../i18n';

export function Totp() {
  const navigate = useNavigate();
  const totp = useTotp();
  const t = useT();
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = (override?: string) => {
    const c = (override ?? code).trim();
    if (!/^\d{6,8}$/.test(c)) return;
    setErr(null);
    totp.mutate(
      { code: c },
      {
        onSuccess: (data) => {
          if (!data.ok) {
            setErr(data.error ?? t('totp.failed'));
            setCode('');
            getTg()?.HapticFeedback.notificationOccurred('error');
            return;
          }
          getTg()?.HapticFeedback.notificationOccurred('success');
        },
        onError: (e) => {
          setErr(String(e));
          getTg()?.HapticFeedback.notificationOccurred('error');
        },
      },
    );
  };

  return (
    <div
      className="fade-in"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--tg-bg)',
        padding: 'calc(var(--safe-top) + 8px) 24px 32px',
      }}
    >
      <button
        onClick={() => navigate(-1)}
        style={{
          color: 'var(--tg-button)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 15,
          fontWeight: 500,
          alignSelf: 'flex-start',
          padding: '8px 0 16px',
        }}
      >
        <ArrowLeft size={20} strokeWidth={2.4} />
        {t('totp.backToLogin')}
      </button>

      <div style={{ flex: 1 }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 18,
            background: 'linear-gradient(135deg, #FF9500 0%, #FF3B30 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            boxShadow: '0 8px 24px rgba(255, 149, 0, 0.3)',
            marginBottom: 24,
          }}
        >
          <ShieldCheck size={28} strokeWidth={2.4} />
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: '-0.03em',
            lineHeight: 1.15,
          }}
        >
          {t('totp.title')}
        </h1>
        <p style={{ margin: '6px 0 32px', color: 'var(--tg-hint)', fontSize: 15 }}>
          {t('totp.subtitle')}
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24 }}>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            maxLength={8}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 8);
              setCode(v);
              if (v.length === 6) submit(v);
            }}
            style={{
              width: '100%',
              maxWidth: 280,
              background: 'var(--tg-secondary-bg)',
              border: 'none',
              borderRadius: 'var(--r-md)',
              padding: '16px 14px',
              fontSize: 28,
              fontWeight: 600,
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              textAlign: 'center',
              letterSpacing: '0.4em',
              outline: 'none',
              color: 'var(--tg-text)',
            }}
            placeholder={t('totp.placeholder')}
          />
        </div>

        {err && (
          <div
            style={{
              color: 'var(--danger)',
              fontSize: 13,
              textAlign: 'center',
              marginBottom: 12,
            }}
          >
            {err}
          </div>
        )}

        <Button
          onClick={() => submit()}
          disabled={!/^\d{6,8}$/.test(code) || totp.isPending}
          loading={totp.isPending}
        >
          {t('totp.submit')}
        </Button>
      </div>

      <p
        style={{
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--tg-hint)',
          margin: 0,
          paddingBottom: 'var(--safe-bottom)',
        }}
      >
        {t('totp.footer')}
      </p>
    </div>
  );
}
